import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Row, Col, Input, Label, Button, FormFeedback } from "reactstrap";
import {
  createSyllabus,
  fetchWeeks,
  fetchDays,
  addWeeklySyllabusThunk,
  addSyllabusDetailThunk,
} from "slices/Course/thunk";
import { AppDispatch } from "store";

interface SyllabusFormProps {
  validation: any;
  isEdit?: boolean;
}

interface WeekRow {
  uiId: number; // local id for UI
  weekid?: number | null;
  title: string;
  keydeliverable: string;
  weekname?: string;
  saved?: boolean;
  open?: boolean;
  weekdetailId?: number | null;
  days: DayRow[];
  dayForm: {
    dayid?: number | null;
    dayname?: string;
    title: string;
    description: string;
  };
}

interface DayRow {
  uiId: number;
  dayid?: number | null;
  dayname?: string;
  title: string;
  description: string;
}

const SyllabusForm: React.FC<SyllabusFormProps> = ({
  validation,
  isEdit = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [hydratedExisting, setHydratedExisting] = useState(false);

  // STATES
  const [saving, setSaving] = useState(false);
  const [savingWeek, setSavingWeek] = useState(false);
  const [savingDay, setSavingDay] = useState(false);
  const [syllabusId, setSyllabusId] = useState<number | null>(null);
  const [syllabusSaved, setSyllabusSaved] = useState(false);
  const [weeksFetched, setWeeksFetched] = useState(false);
  const [daysFetched, setDaysFetched] = useState(false);

  const [addedWeeks, setAddedWeeks] = useState<WeekRow[]>([]);

  const weeks = useSelector((state: any) => state.course.weekData);
  const weeklyLoading = useSelector(
    (state: any) => state.course.weeklySyllabusLoading
  );

  const days = useSelector((state: any) => state.course.dayData);
  const dayLoading = useSelector((state: any) => state.course.dayLoading);

  const normalizeNumber = (value: any) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const serializeWeeksForFormik = (weeksArr: WeekRow[]) =>
    weeksArr.map((w) => ({
      weekid: w.weekid,
      title: w.title,
      keydeliverable: w.keydeliverable,
      weekname: w.weekname,
      saved: w.saved ?? false, // Ensure saved is explicitly false if undefined
      open: w.open,
      weekdetailId: w.weekdetailId,
      days: (w.days || []).map((d) => ({
        dayid: d.dayid,
        dayname: d.dayname,
        title: d.title,
        description: d.description,
      })),
    }));

  const hydrateWeeksFromFormik = (items: any[] = []): WeekRow[] =>
    items.map((w, idx) => {
      const hydratedDays =
        w.days ||
        w.detail ||
        w.syllabusdetail ||
        w.syllabus_detail ||
        [];

      return {
        uiId: Date.now() + idx,
        weekid:
          normalizeNumber(
            w.weekid ??
              w.week_id ??
              w.id ??
              w.weekdetail_id ??
              w.weekdetailId
          ) || undefined,
        title: w.title || w.week_title || "",
        keydeliverable:
          w.keydeliverable || w.week_keydeliverable || w.key_deliverables || "",
        weekname: w.weekname || w.week_name || w.name || "",
        saved: true,
        open: false,
        weekdetailId:
          normalizeNumber(
            w.weekdetailId ??
              w.weekdetail_id ??
              w.weekdetailid ??
              w.week_id ??
              w.id
          ) || null,
        days: hydratedDays.map((d: any, dIdx: number) => ({
          uiId: Date.now() + idx * 100 + dIdx,
          dayid: normalizeNumber(d.dayid ?? d.id) || undefined,
          dayname: d.dayname || d.day || d.name || "",
          title: d.title || "",
          description: d.description || d.Description || "",
        })),
        dayForm: {
          dayid: null,
          dayname: "",
          title: "",
          description: "",
        },
      };
    });

  // Detect existing syllabus ID
  useEffect(() => {
    const existingId =
      validation?.values?.syllabus_id ||
      validation?.values?.syllabus_items?.[0]?.syllabus_id ||
      validation?.values?.syllabus_items?.[0]?.id ||
      null;

    if (existingId && syllabusId !== Number(existingId)) {
      setSyllabusId(Number(existingId));
      setSyllabusSaved(true);
    }
  }, [
    syllabusId,
    validation?.values?.syllabus_id,
    validation?.values?.syllabus_items,
  ]);

  // Fetch weeks once
  useEffect(() => {
    if (syllabusSaved && !weeksFetched) {
      if (!weeks?.length) dispatch(fetchWeeks());
      setWeeksFetched(true);
    }
  }, [dispatch, syllabusSaved, weeksFetched, weeks?.length]);

  // Fetch days once
  useEffect(() => {
    if (syllabusSaved && !daysFetched) {
      if (!days?.length) dispatch(fetchDays());
      setDaysFetched(true);
    }
  }, [dispatch, syllabusSaved, daysFetched, days?.length]);

  // Hydrate existing weeks/days if present in formik (when editing or after reload)
  useEffect(() => {
    if (!syllabusSaved || hydratedExisting || addedWeeks.length > 0) return;
    const formWeeks = validation?.values?.syllabus_items;
    if (Array.isArray(formWeeks) && formWeeks.length) {
      setAddedWeeks(hydrateWeeksFromFormik(formWeeks));
      setHydratedExisting(true);
    }
  }, [syllabusSaved, hydratedExisting, addedWeeks.length, validation?.values?.syllabus_items]);

  // -----------------------------
  // SAVE SYLLABUS
  // -----------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validation.values.course_id) {
      toast.error("Please complete Basic Info first.");
      return;
    }

    const payload = {
      course_id: validation.values.course_id,
      key_deliverables: validation.values.key_deliverables,
      daywise_description: validation.values.daywise_description,
    };

    try {
      setSaving(true);
      const response = await dispatch(createSyllabus(payload) as any);

      if (response?.success) {
        const returnedId =
          response?.data?.id || response?.data || syllabusId || null;

        if (returnedId) {
          setSyllabusId(returnedId);
          validation.setFieldValue("syllabus_id", returnedId);
        }

        setSyllabusSaved(true);
        toast.success("Syllabus saved successfully");
      } else {
        toast.error(response?.message || "Failed to save syllabus");
      }
    } finally {
      setSaving(false);
    }
  };

  // helper to build a blank week row
  const buildEmptyWeekRow = (): WeekRow => ({
    uiId: Date.now() + Math.floor(Math.random() * 1000),
    weekid: undefined,
    title: "",
    keydeliverable: "",
    weekname: "",
    saved: false,
    open: true,
    weekdetailId: null,
    days: [],
    dayForm: {
      dayid: null,
      dayname: "",
      title: "",
      description: "",
    },
  });

  // -----------------------------
  // ADD / SAVE WEEK FLOW
  // -----------------------------
  const appendEmptyWeekRow = () => {
    setAddedWeeks((prev) => {
      const next = [...prev, buildEmptyWeekRow()];
      validation?.setFieldValue?.(
        "syllabus_items",
        serializeWeeksForFormik(next)
      );
      return next;
    });
  };

  const handleAddWeekRow = async () => {
    if (!syllabusSaved || !syllabusId) {
      toast.error("Please save syllabus first.");
      return;
    }

    // If there is an unsaved week with data, treat "Add Week" as a save action
    const pendingIndex = addedWeeks.findIndex((w) => !w.saved);
    if (pendingIndex !== -1) {
      const pendingWeek = addedWeeks[pendingIndex];

      if (
        !pendingWeek.weekid ||
        !pendingWeek.title.trim() ||
        !pendingWeek.keydeliverable.trim()
      ) {
        toast.error("Fill required week fields before adding a new week.");
        setAddedWeeks((prev) => {
          const copy = [...prev];
          copy[pendingIndex].open = true;
          return copy;
        });
        return;
      }

      const saved = await handleSaveWeekRow(pendingWeek, pendingIndex, {
        silent: true,
      });
      if (saved) {
        appendEmptyWeekRow();
      }
      return;
    }

    // No pending week -> just add a fresh one
    appendEmptyWeekRow();
  };

  // -----------------------------
  // SAVE A SPECIFIC WEEK ROW
  // -----------------------------
  const handleSaveWeekRow = async (
    week: WeekRow,
    index: number,
    options?: { silent?: boolean }
  ) => {
    if (!syllabusId) {
      toast.error("Please save syllabus first.");
      return false;
    }

    if (!week.weekid || !week.title.trim() || !week.keydeliverable.trim()) {
      toast.error("Please fill all week fields.");
      return false;
    }

    const payload = {
      weekid: Number(week.weekid),
      syllabus_id: syllabusId,
      title: week.title,
      keydeliverable: week.keydeliverable,
    };

    try {
      setSavingWeek(true);
      const response = await dispatch(addWeeklySyllabusThunk(payload) as any);

      if (response?.success) {
        if (!options?.silent) toast.success("Week added successfully");

        // Backend may return the created row id under different keys
        const rawWeekDetailId =
          response?.data?.weekdetail_id ??
          response?.data?.weekdetailId ??
          response?.data?.week_id ??
          response?.data?.weekId ??
          response?.data?.weekid ??
          response?.data?.id ??
          response?.data;

        const parsedWeekDetailId = Number(rawWeekDetailId);
        const fallbackWeekId = week.weekid ? Number(week.weekid) : null;
        const effectiveWeekDetailId =
          !Number.isNaN(parsedWeekDetailId) && parsedWeekDetailId
            ? parsedWeekDetailId
            : fallbackWeekId;

        if (!effectiveWeekDetailId) {
          toast.error("Could not read week id from server response");
          return false;
        }

        if (
          Number.isNaN(parsedWeekDetailId) ||
          !parsedWeekDetailId ||
          parsedWeekDetailId === null
        ) {
          // warn but continue with fallback so days can still be linked
          toast.warn("Week saved, using selected week id for linking days.");
        }

        // Mark this week row as saved - preserve ALL existing field values (don't reset any fields)
        setAddedWeeks((prev) => {
          // Get the current week from the latest state (not the week parameter which might be stale)
          const currentWeekFromState = prev[index];
          
          const updatedWeeks = prev.map((item, idx) => {
            if (idx === index) {
              // Explicitly preserve ALL fields from the latest state
              return {
                uiId: currentWeekFromState.uiId,
                weekid: currentWeekFromState.weekid,
                weekname: currentWeekFromState.weekname || "",
                title: currentWeekFromState.title || "",
                keydeliverable: currentWeekFromState.keydeliverable || "",
                days: currentWeekFromState.days || [],
                dayForm: currentWeekFromState.dayForm || {
                  dayid: null,
                  dayname: "",
                  title: "",
                  description: "",
                },
                // Only update these fields
                saved: true,
                open: true,
                weekdetailId: Number(effectiveWeekDetailId),
              };
            }
            return item;
          });
          
          // Update formik to keep it in sync with state
          validation?.setFieldValue?.(
            "syllabus_items",
            serializeWeeksForFormik(updatedWeeks)
          );
          
          return updatedWeeks;
        });
        return true;
      } else {
        toast.error(response?.message || "Failed to save week");
        return false;
      }
    } finally {
      setSavingWeek(false);
    }
  };

  // -----------------------------
  // SAVE DAY
  // -----------------------------
  const handleSaveDayForWeek = async (weekIndex: number) => {
    const week = addedWeeks[weekIndex];
    
    // Parse weekdetail_id properly - must be a number
    const targetWeekdetailId = week?.weekdetailId 
      ? Number(week.weekdetailId) 
      : null;

    if (!targetWeekdetailId || isNaN(targetWeekdetailId)) {
      toast.error("Please save the week first before adding days.");
      return;
    }

    const { dayid, title, description } = week.dayForm;

    if (!dayid || !title.trim() || !description.trim()) {
      toast.error("Please fill all day fields.");
      return;
    }

    const payload = {
      weekdetail_id: Number(targetWeekdetailId), // Ensure it's a number
      dayid: Number(dayid),
      title: title.trim(),
      description: description.trim(),
    };

    try {
      setSavingDay(true);
      const response = await dispatch(addSyllabusDetailThunk(payload) as any);

      if (response?.success !== false) {
        toast.success("Day added");

        const selected = days?.find(
          (d: any) => Number(d.id) === Number(dayid)
        );
        const newDay: DayRow = {
          uiId: Date.now() + Math.floor(Math.random() * 1000),
          dayid: Number(dayid),
          dayname: selected?.dayname || "",
          title: title.trim(),
          description: description.trim(),
        };

        setAddedWeeks((prev) =>
          prev.map((item, idx) => {
            if (idx === weekIndex) {
              // Preserve all existing fields and add the new day
              return {
                ...item, // Preserves all week fields (weekid, weekname, title, keydeliverable, etc.)
                days: [...(item.days || []), newDay],
                // Preserve dayForm values - keep fields prefilled after saving
                dayForm: {
                  ...item.dayForm, // Preserves all dayForm fields
                },
              };
            }
            return item;
          })
        );

        setAddedWeeks((prev) => {
          const copy = [...prev];
          validation?.setFieldValue?.(
            "syllabus_items",
            serializeWeeksForFormik(copy)
          );
          return copy;
        });
      } else {
        toast.error(response?.message || "Failed to save day");
      }
    } finally {
      setSavingDay(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* -----------------------------
          BASIC SYLLABUS SECTION
          Hidden after syllabus is saved
      ----------------------------- */}
      {!syllabusSaved && (
        <div className="border rounded p-3 mb-4 shadow-sm">
          <Row className="g-3">
            <Col md={12}>
              <Label>Key Deliverables *</Label>
              <Input
                type="text"
                name="key_deliverables"
                value={validation.values.key_deliverables}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.key_deliverables &&
                  !!validation.errors.key_deliverables
                }
                disabled={isEdit}
              />
              <FormFeedback>
                {validation.errors.key_deliverables}
              </FormFeedback>
            </Col>

            <Col md={12}>
              <Label>Day-wise Description *</Label>
              <Input
                type="textarea"
                rows={3}
                name="daywise_description"
                value={validation.values.daywise_description}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.daywise_description &&
                  !!validation.errors.daywise_description
                }
                disabled={isEdit}
              />
              <FormFeedback>
                {validation.errors.daywise_description}
              </FormFeedback>
            </Col>
          </Row>
        </div>
      )}

      {/* -----------------------------
          WEEK SECTION (MULTIPLE ROWS)
      ----------------------------- */}
      {syllabusSaved && (
        <div className="border rounded p-3 mb-4 shadow-sm">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Weekly Syllabus</h6>
            <Button color="dark" size="sm" outline onClick={handleAddWeekRow}>
              + Add Week
            </Button>
          </div>

          {addedWeeks.map((week, index) => {
            // keep week panel always open to add days right away
            const isOpen = true;
            // Explicitly check saved status - only true if explicitly set to true and has weekdetailId
            const isSaved = week.saved === true && week.weekdetailId !== null && week.weekdetailId !== undefined;

            return (
              <div
                key={week.uiId}
                className="border rounded mb-2 p-2"
                style={{ background: "#fafafa" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <strong>
                      {week.weekname
                        ? week.weekname
                        : `Week ${index + 1}`}
                    </strong>
                    {isSaved && (
                      <span className="badge bg-success ms-2">Saved</span>
                    )}
                    {week.days?.length ? (
                      <span className="badge bg-secondary ms-1">
                        {week.days.length} day{week.days.length > 1 ? "s" : ""}
                      </span>
                    ) : null}
                  </div>

                  {/* Collapse toggle removed to keep week open */}
                </div>

                {isOpen && (
                  <div className="mt-3">
                    <Row className="g-3">
                      <Col md={12}>
                        <Label>Select Week *</Label>
                        <Input
                          type="select"
                          value={week.weekid ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const selected = weeks?.find(
                              (w: any) => Number(w.id) === Number(value)
                            );

                            setAddedWeeks((prev) =>
                              prev.map((item, idx) =>
                                idx === index
                                  ? {
                                      ...item,
                                      weekid: value ? Number(value) : undefined,
                                      weekname: selected?.weekname || "",
                                    }
                                  : item
                              )
                            );
                          }}
                          disabled={isSaved}
                          readOnly={isSaved}
                        >
                          <option value="">Select Week</option>
                          {weeks?.map((w: any) => (
                            <option key={w.id} value={w.id}>
                              {w.weekname}
                            </option>
                          ))}
                        </Input>
                      </Col>

                      <Col md={12}>
                        <Label>Title *</Label>
                        <Input
                          type="text"
                          value={week.title || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setAddedWeeks((prev) =>
                              prev.map((item, idx) =>
                                idx === index ? { ...item, title: value } : item
                              )
                            );
                          }}
                          disabled={isSaved}
                          readOnly={isSaved}
                        />
                      </Col>

                      <Col md={12}>
                        <Label>Key Deliverable *</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          value={week.keydeliverable || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setAddedWeeks((prev) =>
                              prev.map((item, idx) =>
                                idx === index
                                  ? { ...item, keydeliverable: value }
                                  : item
                              )
                            );
                          }}
                          disabled={isSaved}
                          readOnly={isSaved}
                        />
                      </Col>

                      <Col md={12} className="text-end">
                        <Button
                          color="primary"
                          onClick={() => handleSaveWeekRow(week, index)}
                          disabled={
                            savingWeek ||
                            weeklyLoading ||
                            !week.weekid ||
                            !week.title.trim() ||
                            !week.keydeliverable.trim()
                          }
                        >
                          {(savingWeek || weeklyLoading) && (
                            <i className="fa fa-spinner fa-spin me-2" />
                          )}
                          Save Week
                        </Button>
                      </Col>
                    </Row>

                    {/* Add Day Section - Always visible for all weeks */}
                    <div className="mt-4 border-top pt-3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Add Day</h6>
                        <small className="text-muted">
                          {isSaved 
                            ? "Days are linked to this week" 
                            : "Save week first to add days"}
                        </small>
                      </div>

                      <Row className="g-3">
                        <Col md={12}>
                          <Label>Select Day *</Label>
                          <Input
                            type="select"
                            value={week.dayForm.dayid ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const selectedDay = days?.find(
                                (d: any) => Number(d.id) === Number(value)
                              );

                              setAddedWeeks((prev) =>
                                prev.map((item, idx) =>
                                  idx === index
                                    ? {
                                        ...item,
                                        dayForm: {
                                          ...item.dayForm,
                                          dayid: value
                                            ? Number(value)
                                            : null,
                                          dayname:
                                            selectedDay?.dayname || "",
                                        },
                                      }
                                    : item
                                )
                              );
                            }}
                          >
                            <option value="">Select Day</option>
                            {days?.map((day: any) => (
                              <option key={day.id} value={day.id}>
                                {day.dayname}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={12}>
                          <Label>Title *</Label>
                          <Input
                            type="text"
                            value={week.dayForm.title}
                            onChange={(e) => {
                              const value = e.target.value;
                              setAddedWeeks((prev) =>
                                prev.map((item, idx) =>
                                  idx === index
                                    ? {
                                        ...item,
                                        dayForm: {
                                          ...item.dayForm,
                                          title: value,
                                        },
                                      }
                                    : item
                                )
                              );
                            }}
                          />
                        </Col>

                        <Col md={12}>
                          <Label>Key Deliverable *</Label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={week.dayForm.description}
                            onChange={(e) => {
                              const value = e.target.value;
                              setAddedWeeks((prev) =>
                                prev.map((item, idx) =>
                                  idx === index
                                    ? {
                                        ...item,
                                        dayForm: {
                                          ...item.dayForm,
                                          description: value,
                                        },
                                      }
                                    : item
                                )
                              );
                            }}
                          />
                        </Col>

                        <Col md={12} className="text-end">
                          <Button
                            color="primary"
                            onClick={() => handleSaveDayForWeek(index)}
                            disabled={
                              savingDay ||
                              dayLoading ||
                              !week.dayForm.dayid ||
                              !week.dayForm.title.trim() ||
                              !week.dayForm.description.trim()
                            }
                          >
                            {(savingDay || dayLoading) && (
                              <i className="fa fa-spinner fa-spin me-2" />
                            )}
                            Save Day
                          </Button>
                        </Col>
                      </Row>

                      {/* Display saved days */}
                      {week.days?.length > 0 && (
                        <div className="mt-3">
                          <div className="d-flex flex-column gap-2">
                            {week.days.map((day) => (
                              <div
                                key={day.uiId}
                                className="border rounded p-2 bg-white"
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="fw-semibold">
                                    {day.dayname || "Day"}
                                  </span>
                                  {day.dayid && (
                                    <small className="text-muted">
                                      ID: {day.dayid}
                                    </small>
                                  )}
                                </div>
                                <div className="small mt-1">
                                  <div>
                                    <strong>Title:</strong> {day.title}
                                  </div>
                                  <div>
                                    <strong>Key Deliverable:</strong>{" "}
                                    {day.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* SAVE SYLLABUS BUTTON */}
      {!syllabusSaved && (
        <div className="d-flex justify-content-end mt-4">
          <Button
            color="primary"
            type="submit"
            disabled={
              saving ||
              !validation.values.key_deliverables ||
              !validation.values.daywise_description
            }
          >
            {saving && <i className="fa fa-spinner fa-spin me-2" />}
            {isEdit ? "Update Syllabus" : "Save"}
          </Button>
        </div>
      )}
    </form>
  );
};
export default SyllabusForm;  
